import React, { useState, useEffect } from 'react';
import { MagnifyingGlassIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { useClasses } from './hooks';
import { CalendarView, ClassBookingModal } from './components';
import '../../styles/components.css';

/**
 * Classes page - displays calendar view of upcoming classes
 */
const Classes = () => {
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);

  const {
    classes,
    loading,
    selectedClass,
    user,
    fetchClasses,
    selectClass,
    clearSelectedClass,
    bookClass,
    cancelBooking,
    joinWaitlist,
    leaveWaitlist,
    isWithin30Minutes,
    isWithin1Hour,
    isWithin24Hours,
    isAtConcessionLimit,
    canJoinWaitlist,
    isUserBooked,
    isUserOnWaitlist
  } = useClasses();

  // Fetch classes on mount and when search changes
  useEffect(() => {
    fetchClasses(search);
  }, [fetchClasses, search]);

  // Handle class click - select class and open modal
  const handleClassClick = async (classItem) => {
    await selectClass(classItem);
    setShowModal(true);
  };

  // Handle modal close
  const handleCloseModal = () => {
    setShowModal(false);
    clearSelectedClass();
    fetchClasses(search);
  };

  // Handle booking actions with modal close
  const handleBook = async (classItem) => {
    const success = await bookClass(classItem);
    if (success) {
      handleCloseModal();
    }
  };

  const handleCancel = async (classItem) => {
    const success = await cancelBooking(classItem);
    if (success) {
      handleCloseModal();
    }
  };

  const handleJoinWaitlist = async (classItem) => {
    const success = await joinWaitlist(classItem);
    if (success) {
      handleCloseModal();
    }
  };

  const handleLeaveWaitlist = async (classItem) => {
    const success = await leaveWaitlist(classItem);
    if (success) {
      handleCloseModal();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-card rounded-xl shadow-sm border border-primary p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-primary">Upcoming Classes</h1>
              <p className="text-secondary mt-1">
                Discover and book your next workout session
              </p>
            </div>
          </div>

          {/* Search Bar */}
          <div className="mt-6">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted" />
              <input
                type="text"
                placeholder="Search classes, instructors, or descriptions..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="search-input"
              />
            </div>
          </div>
        </div>

        {/* Calendar View */}
        <CalendarView
          classes={classes}
          onClassClick={handleClassClick}
          isUserBooked={isUserBooked}
          isUserOnWaitlist={isUserOnWaitlist}
        />

        {/* Empty State */}
        {classes.length === 0 && (
          <div className="text-center py-12">
            <CalendarIcon className="mx-auto h-12 w-12 text-muted" />
            <h3 className="mt-2 text-sm font-medium text-primary">No classes found</h3>
            <p className="mt-1 text-sm text-secondary">
              Try adjusting your filters or search terms.
            </p>
          </div>
        )}
      </div>

      {/* Booking Modal */}
      <ClassBookingModal
        isOpen={showModal && selectedClass !== null}
        onClose={handleCloseModal}
        classData={selectedClass}
        user={user}
        onBook={handleBook}
        onCancel={handleCancel}
        onJoinWaitlist={handleJoinWaitlist}
        onLeaveWaitlist={handleLeaveWaitlist}
        isWithin30Minutes={isWithin30Minutes}
        isWithin1Hour={isWithin1Hour}
        isWithin24Hours={isWithin24Hours}
        isAtConcessionLimit={isAtConcessionLimit}
        canJoinWaitlist={canJoinWaitlist}
        isUserBooked={isUserBooked}
        isUserOnWaitlist={isUserOnWaitlist}
      />
    </>
  );
};

export default Classes;
